import { Tooltip } from "@/components/TooltipV2";
import { ButtonSize } from "@/components/interactive/Button";
import { IconButton, IconButtonColor } from "@/components/interactive/IconButton";
import { PropsWithClassName } from "@/interfaces/with-classname";
import { classNames } from "@/utils/classnames";
import { prettyFormatDateTime } from "@/utils/date-time";
import React from "react";

export interface RefreshButtonProps extends PropsWithClassName {
  refresh: () => void;
  loading: boolean;
  size?: ButtonSize;
  color?: IconButtonColor;
  /**
   * A numeric timestamp representing the unix time in millis that the data this button refreshes was last updated.
   * We strongly prefer that you provide this value if possible, because it provides observability into how
   * stale the data is, which increases trust in the UI.
   */
  lastUpdated: number | undefined;
  lastUpdatedStyle?: "tooltip" | "text";
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  refresh,
  loading,
  size = "sm",
  className,
  color,
  lastUpdated,
  lastUpdatedStyle = "tooltip",
}) => {
  const content = (
    <IconButton
      size={size}
      iconName={"retry"}
      title="Refresh"
      onClick={() => refresh()}
      disabled={loading}
      className={className}
      color={color}
      type={"button"}
      iconClassName={classNames(loading && "animate-spin")}
    />
  );

  if (lastUpdated != null) {
    if (lastUpdatedStyle === "tooltip") {
      return (
        <Tooltip
          content={`Updated ${prettyFormatDateTime(new Date(lastUpdated).toLocaleString(), { caps: false })}`}
          instant
          style={{ maxWidth: 800 }}
        >
          {content}
        </Tooltip>
      );
    } else if (lastUpdatedStyle === "text") {
      return (
        <div className={"flex items-center gap-0.5"}>
          <span className="text-xs text-gray-500">
            Updated {prettyFormatDateTime(new Date(lastUpdated).toLocaleString(), { caps: false })}
          </span>
          {content}
        </div>
      );
    }
  }

  return content;
};
